/**
 * Recall module hooks (C-3). Backed by /api/v1/recalls (real Postgres,
 * recall.recalls). A recall links to complaints / deviations / CAPAs via the
 * C-4 event graph (the RelatedRecords panel on the detail screen).
 */
import {
  useMutation, useQuery, useQueryClient,
  type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface RecallHistoryRow {
  event_kind: string; from_status: string | null; to_status: string | null;
  note: string | null; at_utc: string; actor_name: string | null;
}
export interface Recall {
  recallId: string;
  recallNumber: string;
  productName: string;
  batchRefs: string | null;
  classification: string;
  markets: string | null;
  reason: string;
  status: string;
  qtyReleased: number | null;
  qtyRecovered: number | null;
  effectivenessPct: number | null;
  healthHazardEvaluation: string | null;
  regulatoryNotification: string | null;
  isMock: boolean;
  ownerName: string | null;
  openedAt: string;
  closedAt: string | null;
  history?: RecallHistoryRow[];
}
export interface RecallStats { total: number; open: number; class1_open: number; mock: number; avg_effectiveness: string | number }
export interface RecallListResp { items: Recall[]; total: number; stats: RecallStats }

export interface RecallFilters { status?: string; classification?: string; mock?: string }

export function useRecallsQuery(filters: RecallFilters = {}): UseQueryResult<RecallListResp> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.classification) qs.set('classification', filters.classification);
  if (filters.mock) qs.set('mock', filters.mock);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<RecallListResp>({
    queryKey: ['recalls', filters],
    queryFn: ({ signal }) => apiFetch<RecallListResp>(`/api/v1/recalls${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useRecallDetailQuery(id: string | undefined): UseQueryResult<Recall> {
  return useQuery<Recall>({
    queryKey: ['recalls', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Recall>(`/api/v1/recalls/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export interface NewRecallInput {
  productName: string; reason: string; batchRefs?: string; classification?: string;
  markets?: string; qtyReleased?: number; isMock?: boolean;
}
export function useCreateRecall(): UseMutationResult<Recall, Error, NewRecallInput> {
  const qc = useQueryClient();
  return useMutation<Recall, Error, NewRecallInput>({
    mutationFn: (body) => apiFetch<Recall>('/api/v1/recalls', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['recalls'] }),
  });
}
export function useRecallTransition(id: string | undefined): UseMutationResult<unknown, Error, { toStatus: string; note?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; note?: string }) =>
      apiFetch(`/api/v1/recalls/${encodeURIComponent(id!)}/transition`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['recalls'] }),
  });
}
export function useRecallReconcile(id: string | undefined): UseMutationResult<unknown, Error, { qtyRecovered: number; qtyReleased?: number }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { qtyRecovered: number; qtyReleased?: number }) =>
      apiFetch(`/api/v1/recalls/${encodeURIComponent(id!)}/reconcile`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['recalls'] }),
  });
}

export const RECALL_FSM: Record<string, string[]> = {
  INITIATED: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['RECONCILIATION', 'CANCELLED'],
  RECONCILIATION: ['CLOSED', 'IN_PROGRESS'],
  CLOSED: [],
  CANCELLED: [],
};
export const STATUS_LABEL: Record<string, string> = {
  INITIATED: 'Initiated', IN_PROGRESS: 'In progress', RECONCILIATION: 'Reconciliation',
  CLOSED: 'Closed', CANCELLED: 'Cancelled',
};
export const CLASS_LABEL: Record<string, string> = {
  CLASS_I: 'Class I', CLASS_II: 'Class II', CLASS_III: 'Class III',
};
